import {Fade} from '@aboutyou/transitions';
import {styled} from '@linaria/react';
import React, {PropsWithChildren} from 'react';
import {COLORS} from '../../../constants/colors';
import {Text} from '../Text/Text';

export type BadgeCountVariant = 'highlight' | 'default' | 'rounded';

export const WithBadge = React.forwardRef<
  HTMLElement,
  PropsWithChildren<{
    displayBadgeWithoutCount?: boolean;
    count?: number;
    variant?: BadgeCountVariant;
    dataTestId?: string;
    offset?: {right?: number; top?: number} | number;
    badgeSize?: number;
    badgeColor?: string | null;
    fadeDuration?: number;
  }>
>(function WithBadge(
  {
    displayBadgeWithoutCount,
    count,
    children,
    variant = 'default',
    dataTestId,
    offset,
    badgeSize,
    badgeColor,
    fadeDuration = 0,
  },
  ref,
) {
  const displayBadge =
    displayBadgeWithoutCount || (count !== undefined && count > 0);
  return (
    <BadgeContainer ref={ref}>
      <Fade mounted={displayBadge} duration={fadeDuration}>
        <Badge
          variant={variant}
          badgeSize={badgeSize}
          badgeColor={badgeColor}
          offset={offset}
          data-testid="badgeDot"
        >
          <Text size="px8" color="White" data-testid={dataTestId}>
            {count}
          </Text>
        </Badge>
      </Fade>
      {children}
    </BadgeContainer>
  );
});

const BadgeContainer = styled.span`
  position: relative;
  top: 0;
  box-sizing: border-box;
`;

const Badge = styled.span<{
  variant?: BadgeCountVariant;
  offset?: {right?: number; top?: number} | number;
  badgeSize?: number;
  badgeColor?: string | null;
}>`
  position: absolute;
  right: ${({offset}) => {
    if (offset) {
      return typeof offset === 'number' ? offset : offset.right!;
    }
    return -6;
  }}px;
  top: ${({offset}) => {
    if (offset) {
      return typeof offset === 'number' ? offset : offset.top!;
    }
    return -6;
  }}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: 2px;
  background-color: ${({variant, badgeColor}) =>
    badgeColor ?? (variant === 'highlight' ? COLORS.RedSale : 'unset')};
  height: ${({badgeSize}) => badgeSize ?? 14}px;
  min-height: ${({badgeSize}) => badgeSize ?? 14}px;
  width: ${({badgeSize}) => badgeSize ?? 14}px;
  min-width: ${({badgeSize}) => badgeSize ?? 14}px;
  border-radius: ${({variant}) => (variant === 'rounded' ? '50%' : '0')};
`;
